import React from 'react'
import { Link, WindowLocation } from '@reach/router'
import { MdxFrontmatterLinks } from 'generated/types/gatsby'

type FileNode = {
  node: {
    name: string
    relativeDirectory: string
  }
}

type VersionData = {
  major: number
  minor: number
  patch: number
  numVersion: number
  semver: string
}

type LinkData = {
  name: string
  relativeDirectory?: string
  versionData: VersionData
  link?: string
  isNew?: boolean
  isLts?: boolean
}

const INVALID_VERSION = -1

interface Props {
  subcategory: string
  allFiles: FileNode[]
  allLinks: AllLinks
  location: WindowLocation
  productName: string
}

type AllLinks = {
  __typename?: 'MdxFrontmatterLinks' | undefined
} & Pick<
  MdxFrontmatterLinks,
  'title' | 'path' | 'top' | 'header' | 'pdf' | 'category' | 'isNew' | 'isLts'
>[]

export function ReleaseNotes(props: Props) {
  const { allFiles, allLinks, productName } = props
  let currentMajorVer = INVALID_VERSION

  const getMajorReleaseLabel = (version: number): string => {
    return `${productName} ${version}.x`
  }

  const getVersionDataByRegexp = (fileName: string): VersionData => {
    const version: VersionData = {
      major: INVALID_VERSION,
      minor: INVALID_VERSION,
      patch: INVALID_VERSION,
      numVersion: INVALID_VERSION,
      semver: INVALID_VERSION.toString(),
    }
    const indexOfZeroBasedMatchIndex = 4
    // This regexp returns substring in x.x.x format, where x is an integer
    const data: RegExpMatchArray | null = fileName.match(/^(\d+)\.(\d+)\.(\d+)/)
    if (!data || data.length < indexOfZeroBasedMatchIndex) return version

    version.major = parseInt(data[1])
    version.minor = parseInt(data[2])
    version.patch = parseInt(data[3])
    version.numVersion = parseInt(`${version.major}${version.minor}${version.patch}`)
    version.semver = data[0]

    return version
  }

  const getFormattedFilesData = (files: FileNode[], links: AllLinks): LinkData[] => {
    const filesData = files.map((file: FileNode) => {
      const versionData = getVersionDataByRegexp(file.node.name)
      return {
        name: file.node.name,
        relativeDirectory: file.node.relativeDirectory,
        versionData,
      }
    })

    const linksData = links.map((link) => {
      const versionData = getVersionDataByRegexp(link.title)
      return {
        name: link.title,
        link: link.path,
        versionData,
        isNew: link.isNew,
        isLts: link.isLts,
      }
    })

    return [...filesData, ...linksData]
  }

  const versionComparator = (prev: LinkData, next: LinkData): number => {
    return next.versionData.numVersion - prev.versionData.numVersion
  }

  const sortFilesDataByVersions = (initFiles: LinkData[]): LinkData[] => {
    const result = [...initFiles]
    result.sort(versionComparator)
    return result
  }

  const getMinorVersionsLinks = (
    majorVersion: number,
    linksData: LinkData[],
    isMajorVersionLatest: boolean
  ): (JSX.Element | undefined)[] | undefined => {
    const minorVersionsGroups: { [key: string]: LinkData[] } = linksData
      .filter((linkData) => linkData.versionData.major === majorVersion)
      .reduce((group: { [key: string]: LinkData[] }, linkData: LinkData) => {
        const groupName = `${linkData.versionData.major}.${linkData.versionData.minor}`
        group[groupName] = group[groupName] ?? []
        group[groupName].push(linkData)
        return group
      }, {})

    return Object.entries(minorVersionsGroups).map(
      ([groupName, linksData]: [string, LinkData[]]) => {
        const isMinorVersionLatest =
          isMajorVersionLatest && Object.keys(minorVersionsGroups)[0] === groupName
        const hasLtsChild: boolean = linksData.findIndex((link) => link.isLts) > -1
        const hasNewChild: boolean = linksData.findIndex((link) => link.isNew) > -1

        if (linksData.length > 1)
          return (
            <div className={'guides-subcategory guides-subcategory-list__item'} key={Math.random()}>
              <input
                className={'guides-subcategory__checkbox'}
                type="checkbox"
                name="minorVer"
                value={groupName}
                id={groupName}
                defaultChecked={isMinorVersionLatest}
              />

              <label
                htmlFor={groupName}
                className={'guides-subcategory__label guides-subcategory-major-ver'}
                id={'#' + groupName}
              >
                {groupName}
                {hasLtsChild && <sup className="guides-mark">LTS</sup>}
                {hasNewChild && <sup className="guides-mark">NEW</sup>}
              </label>

              <div className={'guides-subcategory__links guides-link-set'}>
                {linksData.map((linkData) => {
                  const {
                    name,
                    relativeDirectory,
                    versionData: { semver },
                    link,
                    isNew,
                  } = linkData
                  return (
                    (link && (
                      <Link
                        className={'guides-link-set__item la-icon la-icon_document'}
                        key={`${semver}`}
                        to={`${productName}/${link}`}
                      >
                        {semver}
                        {isNew && <sup className="guides-mark">NEW</sup>}
                      </Link>
                    )) || (
                      <a
                        className={'guides-link-set__item la-icon la-icon_download'}
                        href={`/pdf/${relativeDirectory}/${name}.pdf`}
                        key={`${semver}`}
                        download
                      >
                        {semver}
                        {isNew && <sup className="guides-mark">NEW</sup>}
                      </a>
                    )
                  )
                })}
              </div>
            </div>
          )

        const {
          name,
          relativeDirectory,
          versionData: { semver },
          link,
          isNew,
          isLts,
        } = linksData[0]

        return (
          (link && (
            <Link
              className={'guides-link-set__item la-icon la-icon_document'}
              key={`${semver}`}
              to={`${productName}/${link}`}
            >
              {semver}
              {isLts && <sup className="guides-mark">LTS</sup>}
              {isNew && <sup className="guides-mark">NEW</sup>}
            </Link>
          )) || (
            <a
              className={'guides-link-set__item la-icon la-icon_download'}
              href={`/pdf/${relativeDirectory}/${name}.pdf`}
              key={`${semver}`}
              download
            >
              {semver}
              {isLts && <sup className="guides-mark">LTS</sup>}
              {isNew && <sup className="guides-mark">NEW</sup>}
            </a>
          )
        )
      },
      []
    )
  }

  const filesData = getFormattedFilesData(allFiles, allLinks)
  const sortedFilesData = sortFilesDataByVersions(filesData)

  return (
    <div>
      {sortedFilesData.map((linkData: LinkData) => {
        if (!linkData.name) return
        const fileMajorVer = linkData.versionData.major
        if (fileMajorVer === INVALID_VERSION) return
        if (fileMajorVer !== currentMajorVer) {
          currentMajorVer = fileMajorVer
          const majVerId = currentMajorVer.toString()
          const majLabel = getMajorReleaseLabel(currentMajorVer)

          const isMajorVersionLatest = sortedFilesData[0].versionData.major === currentMajorVer
          const currentMajorVerLinks = sortedFilesData.filter(
            (linkData) => linkData.versionData.major === currentMajorVer
          )
          const hasLtsChild: boolean = currentMajorVerLinks.findIndex((link) => link.isLts) > -1
          const hasNewChild: boolean = currentMajorVerLinks.findIndex((link) => link.isNew) > -1

          return (
            <div className={'guides-subcategory guides-subcategory-list__item'} key={Math.random()}>
              <input
                className={'guides-subcategory__checkbox'}
                type="checkbox"
                name="majorVer"
                value={majLabel}
                id={majVerId}
                defaultChecked={isMajorVersionLatest}
              />
              <label
                htmlFor={majVerId}
                className={'guides-subcategory__label guides-subcategory-major-ver'}
                id={'#' + majVerId}
              >
                {majLabel}
                {hasLtsChild && <sup className="guides-mark">LTS</sup>}
                {hasNewChild && <sup className="guides-mark">NEW</sup>}
              </label>
              <div className={'guides-subcategory__links guides-link-set'}>
                {getMinorVersionsLinks(currentMajorVer, sortedFilesData, isMajorVersionLatest)}
              </div>
            </div>
          )
        }
      })}
    </div>
  )
}
